import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Pencil, HardHat, Filter, Upload, FileText, History } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const STAGES = [
  { key: "cotacao", label: "Cotacao" },
  { key: "codigo", label: "Codigo" },
  { key: "lista_material", label: "Lista de Material" },
  { key: "desenho", label: "Desenho" },
  { key: "liberacao_material", label: "Lib. Material" },
];

const STATUS_OPTIONS = [
  { value: "", label: "-", color: "bg-muted text-muted-foreground" },
  { value: "feito", label: "Feito", color: "bg-green-500 text-white" },
  { value: "produzindo", label: "Produzindo", color: "bg-yellow-400 text-yellow-900" },
  { value: "nao_liberado", label: "Nao liberado", color: "bg-red-500 text-white" },
  { value: "liberado", label: "Liberado", color: "bg-blue-500 text-white" },
];

const getStatusStyle = (value) => STATUS_OPTIONS.find((s) => s.value === value) || STATUS_OPTIONS[0];

const StatusBadge = ({ status, small }) => {
  const s = getStatusStyle(status);
  if (!status) return <span className="text-xs text-muted-foreground">-</span>;
  return <Badge className={`${s.color} ${small ? "text-[9px] px-1.5 py-0" : "text-[10px] px-2 py-0.5"}`}>{s.label}</Badge>;
};

export default function EngineeringPage() {
  const { can } = useAuth();
  const isAdmin = can("engenharia", "edit");
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [filterClient, setFilterClient] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [obraFilter, setObraFilter] = useState("");
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyProject, setHistoryProject] = useState(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  const emptyForm = {
    client_id: "", obra: "", approved_date: "", observations: "",
    cotacao: { status: "", date: "" },
    codigo: { status: "", date: "" },
    lista_material: { status: "", date: "" },
    desenho: { status: "", date: "" },
    liberacao_material: { status: "", date: "" },
  };
  const [form, setForm] = useState(emptyForm);

  const fetchProjects = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/engineering`, { withCredentials: true });
      setProjects(data);
    } catch (err) { console.error("fetchEngineering:", err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchProjects();
    axios.get(`${API}/clients`, { withCredentials: true }).then(({ data }) => setClients(data)).catch((err) => console.error("fetchClients:", err));
  }, [fetchProjects]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setFiles([]); setDialogOpen(true); };

  const openEdit = (p) => {
    setEditing(p);
    setFiles(p.files || []);
    setForm({
      client_id: p.client_id || "",
      obra: p.obra || "",
      approved_date: p.approved_date || "",
      observations: p.observations || "",
      cotacao: { status: p.cotacao?.status || "", date: p.cotacao?.date || "" },
      codigo: { status: p.codigo?.status || "", date: p.codigo?.date || "" },
      lista_material: { status: p.lista_material?.status || "", date: p.lista_material?.date || "" },
      desenho: { status: p.desenho?.status || "", date: p.desenho?.date || "" },
      liberacao_material: { status: p.liberacao_material?.status || "", date: p.liberacao_material?.date || "" },
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.client_id) { toast.error("Selecione um cliente"); return; }
    if (!form.obra.trim()) { toast.error("Informe a obra"); return; }
    try {
      if (editing) {
        await axios.put(`${API}/engineering/${editing.id}`, form, { withCredentials: true });
        toast.success("Projeto atualizado");
      } else {
        await axios.post(`${API}/engineering`, form, { withCredentials: true });
        toast.success("Projeto criado");
      }
      setDialogOpen(false);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/engineering/${deleteTarget}`, { withCredentials: true });
      toast.success("Projeto removido");
      setDeleteConfirmOpen(false);
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao excluir"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !editing) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/engineering/${editing.id}/upload`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      setFiles((prev) => [...prev, data]);
      toast.success("Arquivo enviado");
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao enviar arquivo"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const downloadFile = async (fileRef) => {
    try {
      const response = await axios.get(`${API}/engineering/${editing.id}/files/${fileRef.id}`, { withCredentials: true, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a"); a.href = url; a.download = fileRef.original_filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Erro ao baixar arquivo"); }
  };

  const handleDeleteFile = async (fileId) => {
    if (!editing) return;
    try {
      await axios.delete(`${API}/engineering/${editing.id}/files/${fileId}`, { withCredentials: true });
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
      toast.success("Arquivo removido");
      fetchProjects();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao remover arquivo"); }
  };

  // Quick inline status update (responsible + date auto-filled by backend)
  const handleQuickStatus = async (project, stageKey, newStatus) => {
    const stageData = project[stageKey] || {};
    try {
      await axios.put(`${API}/engineering/${project.id}`, { [stageKey]: { status: newStatus, date: stageData.date || "" } }, { withCredentials: true });
      fetchProjects();
    } catch (err) { toast.error("Erro ao atualizar status"); }
  };

  const openHistory = (p) => { setHistoryProject(p); setHistoryOpen(true); };

  const updateFormStage = (stageKey, field, value) => {
    setForm((prev) => ({ ...prev, [stageKey]: { ...prev[stageKey], [field]: value } }));
  };

  // Filters
  const filtered = projects.filter((p) => {
    if (filterClient !== "all" && p.client_id !== filterClient) return false;
    if (obraFilter.trim() && !(p.obra || "").toLowerCase().includes(obraFilter.trim().toLowerCase())) return false;
    if (filterStatus !== "all") {
      const hasStatus = STAGES.some((s) => (p[s.key]?.status || "") === filterStatus);
      if (!hasStatus) return false;
    }
    return true;
  });

  // Summary counters
  const summary = {};
  STAGES.forEach((s) => {
    summary[s.key] = { feito: 0, produzindo: 0, nao_liberado: 0, liberado: 0 };
    projects.forEach((p) => {
      const st = p[s.key]?.status;
      if (st && summary[s.key][st] !== undefined) summary[s.key][st]++;
    });
  });

  return (
    <div className="space-y-6 animate-slide-in" data-testid="engineering-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Engenharia</h1>
          <p className="text-sm text-muted-foreground mt-1">Controle de projetos e etapas de engenharia</p>
        </div>
        <div className="flex gap-2">
          <Select value={filterClient} onValueChange={setFilterClient}>
            <SelectTrigger className="w-48" data-testid="eng-filter-client"><SelectValue placeholder="Cliente" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os clientes</SelectItem>
              {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-40" data-testid="eng-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              {STATUS_OPTIONS.filter((s) => s.value).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
            </SelectContent>
          </Select>
            {can("engenharia", "create") && (
            <Button onClick={openCreate} data-testid="eng-create-btn">
              <Plus className="h-4 w-4 mr-2" /> Novo Projeto
            </Button>
            )}
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {STAGES.map((s) => (
          <Card key={s.key} className="border shadow-none">
            <CardContent className="p-3">
              <p className="text-xs font-semibold text-muted-foreground uppercase mb-2">{s.label}</p>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2"><Badge className="bg-green-500 text-white text-[10px] w-5 h-5 flex items-center justify-center p-0 rounded-full">{summary[s.key].feito}</Badge><span className="text-xs">Feito</span></div>
                <div className="flex items-center gap-2"><Badge className="bg-yellow-400 text-yellow-900 text-[10px] w-5 h-5 flex items-center justify-center p-0 rounded-full">{summary[s.key].produzindo}</Badge><span className="text-xs">Produzindo</span></div>
                <div className="flex items-center gap-2"><Badge className="bg-red-500 text-white text-[10px] w-5 h-5 flex items-center justify-center p-0 rounded-full">{summary[s.key].nao_liberado}</Badge><span className="text-xs">Nao Liberado</span></div>
                <div className="flex items-center gap-2"><Badge className="bg-blue-500 text-white text-[10px] w-5 h-5 flex items-center justify-center p-0 rounded-full">{summary[s.key].liberado}</Badge><span className="text-xs">Liberado</span></div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Projects table */}
      <Card className="border shadow-none overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px] sticky left-0 bg-background z-10">Cliente</TableHead>
                  <TableHead className="min-w-[140px]">Obra</TableHead>
                  {STAGES.map((s) => <TableHead key={s.key} className="min-w-[130px] text-center">{s.label}</TableHead>)}
                  <TableHead className="min-w-[100px] text-center">Pedido Aprov.</TableHead>
                  <TableHead className="min-w-[140px]">Ultima Atualizacao</TableHead>
                  <TableHead className="min-w-[150px]">Observacoes</TableHead>
                  {isAdmin && <TableHead className="w-[100px]"></TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={10 + (isAdmin ? 1 : 0)} className="text-center py-8 text-muted-foreground">Carregando...</TableCell></TableRow>
                ) : filtered.length === 0 ? (
                  <TableRow><TableCell colSpan={10 + (isAdmin ? 1 : 0)} className="text-center py-8 text-muted-foreground">Nenhum projeto encontrado</TableCell></TableRow>
                ) : (
                  filtered.map((p) => (
                    <TableRow key={p.id} data-testid={`eng-row-${p.id}`}>
                      <TableCell className="sticky left-0 bg-background z-10 font-medium text-sm">
                        <div>{p.client_code ? `${p.client_code} - ` : ""}{p.client_name}</div>
                      </TableCell>
                      <TableCell className="text-sm" data-testid={`eng-obra-${p.id}`}>{p.obra || "-"}</TableCell>
                      {STAGES.map((s) => {
                        const stage = p[s.key] || {};
                        return (
                          <TableCell key={s.key} className="text-center">
                            {isAdmin ? (
                              <Select value={stage.status || "empty"} onValueChange={(v) => handleQuickStatus(p, s.key, v === "empty" ? "" : v)}>
                                <SelectTrigger className="h-7 w-full border-0 justify-center" data-testid={`eng-${p.id}-${s.key}`}>
                                  <StatusBadge status={stage.status} />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="empty">-</SelectItem>
                                  {STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                                    <SelectItem key={o.value} value={o.value}><Badge className={`${o.color} text-[10px]`}>{o.label}</Badge></SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            ) : (
                              <StatusBadge status={stage.status} />
                            )}
                            {stage.date && <div className="text-[9px] text-muted-foreground mt-0.5">{format(new Date(stage.date), "dd/MM/yy")}</div>}
                            {stage.completed_by && <div className="text-[9px] text-muted-foreground">{stage.completed_by}</div>}
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center text-xs">{p.approved_date ? format(new Date(p.approved_date), "dd/MM/yy") : "-"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {p.last_updated_by ? (
                          <div>
                            <div className="font-medium text-foreground">{p.last_updated_by}</div>
                            <div className="text-[10px]">{p.updated_at ? format(new Date(p.updated_at), "dd/MM/yy HH:mm") : ""}</div>
                          </div>
                        ) : "-"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px]"><span className="block truncate">{p.observations || "-"}</span></TableCell>
                      {isAdmin && (
                        <TableCell>
                          <div className="flex gap-0.5">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openHistory(p)} title="Historico" data-testid={`eng-history-${p.id}`}>
                              <History className="h-3 w-3" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`eng-edit-${p.id}`}>
                              <Pencil className="h-3 w-3" />
                            </Button>
                            {can("engenharia", "delete") && (
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => { setDeleteTarget(p.id); setDeleteConfirmOpen(true); }} data-testid={`eng-delete-${p.id}`}>
                              <Trash2 className="h-3 w-3" />
                            </Button>
                            )}
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Projeto" : "Novo Projeto"}</DialogTitle>
            <DialogDescription>Preencha os dados do projeto de engenharia</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Client + obra + dates */}
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Cliente *</Label>
                <Select value={form.client_id || "none"} onValueChange={(v) => setForm({ ...form, client_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="eng-client-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Selecione</SelectItem>
                    {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.client_code ? `${c.client_code} - ` : ""}{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Obra *</Label>
                <Input value={form.obra} onChange={(e) => setForm({ ...form, obra: e.target.value })}
                  placeholder="Nome da obra" data-testid="eng-obra-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Pedido Aprovado</Label>
                <Input type="date" value={form.approved_date ? form.approved_date.substring(0, 10) : ""}
                  onChange={(e) => setForm({ ...form, approved_date: e.target.value ? e.target.value + "T12:00:00Z" : "" })}
                  data-testid="eng-approved-date" />
              </div>
            </div>

            {/* Stages */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs font-semibold uppercase text-muted-foreground">Etapas</Label>
                <span className="text-[10px] text-muted-foreground">Responsavel preenchido automaticamente</span>
              </div>
              {STAGES.map((s) => (
                <div key={s.key} className="grid grid-cols-[140px_1fr_1fr] gap-2 items-center">
                  <span className="text-xs font-medium">{s.label}</span>
                  <Select value={form[s.key]?.status || "empty"} onValueChange={(v) => updateFormStage(s.key, "status", v === "empty" ? "" : v)}>
                    <SelectTrigger className="h-8" data-testid={`eng-form-${s.key}-status`}><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="empty">-</SelectItem>
                      {STATUS_OPTIONS.filter((o) => o.value).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Input type="date" value={form[s.key]?.date ? form[s.key].date.substring(0, 10) : ""}
                    onChange={(e) => updateFormStage(s.key, "date", e.target.value ? e.target.value + "T12:00:00Z" : "")}
                    className="h-8 text-xs" data-testid={`eng-form-${s.key}-date`} />
                </div>
              ))}
            </div>

            {/* Observations */}
            <div className="space-y-1">
              <Label className="text-xs">Observacoes</Label>
              <Textarea value={form.observations} onChange={(e) => setForm({ ...form, observations: e.target.value })}
                rows={2} className="text-xs resize-none" placeholder="Observacoes gerais do projeto" data-testid="eng-observations" />
            </div>

            {/* Attachments (only when editing an existing project) */}
            {editing ? (
              <div className="space-y-2 border-t border-border pt-3">
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold uppercase text-muted-foreground">Anexos</Label>
                  <label className="cursor-pointer">
                    <input type="file" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="eng-file-upload-input" />
                    <Button variant="outline" size="sm" asChild disabled={uploading}>
                      <span><Upload className="h-3 w-3 mr-1" />{uploading ? "Enviando..." : "Upload"}</span>
                    </Button>
                  </label>
                </div>
                {files.length === 0 ? (
                  <p className="text-xs text-muted-foreground">Nenhum arquivo anexado</p>
                ) : (
                  <div className="space-y-1">
                    {files.map((f) => (
                      <div key={f.id} className="flex items-center justify-between p-2 rounded border border-border text-sm hover:bg-muted/50 transition-colors" data-testid={`eng-file-${f.id}`}>
                        <button type="button" className="flex items-center gap-2 min-w-0 text-left" onClick={() => downloadFile(f)} data-testid={`eng-file-download-${f.id}`}>
                          <FileText className="h-4 w-4 text-primary shrink-0" />
                          <span className="truncate">{f.original_filename}</span>
                        </button>
                        <div className="flex items-center gap-2 shrink-0">
                          <span className="text-xs text-muted-foreground hidden sm:block">{f.uploaded_by}</span>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDeleteFile(f.id)} data-testid={`eng-file-delete-${f.id}`}>
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground border-t border-border pt-3">Salve o projeto para anexar arquivos.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={!form.client_id || !form.obra.trim()} data-testid="eng-save-btn">
              {editing ? "Salvar" : "Criar Projeto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Confirmar Exclusao</DialogTitle>
            <DialogDescription>Deseja excluir este projeto?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="eng-confirm-delete">Excluir</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History Dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="eng-history-dialog">
          <DialogHeader>
            <DialogTitle>Historico de Alteracoes</DialogTitle>
            <DialogDescription>
              {historyProject ? `${historyProject.client_name}${historyProject.obra ? " - " + historyProject.obra : ""}` : ""}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(!historyProject?.history || historyProject.history.length === 0) ? (
              <p className="text-sm text-muted-foreground">Nenhuma alteracao registrada.</p>
            ) : (
              [...historyProject.history].reverse().map((h) => (
                <div key={h.id} className="border-l-2 border-primary pl-3 py-1" data-testid={`eng-history-entry-${h.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">{h.by}</span>
                    <span className="text-[11px] text-muted-foreground">{h.at ? format(new Date(h.at), "dd/MM/yy HH:mm") : ""}</span>
                  </div>
                  <ul className="mt-1 space-y-0.5">
                    {(h.changes || []).map((c, i) => (
                      <li key={i} className="text-xs text-muted-foreground">• {c}</li>
                    ))}
                  </ul>
                </div>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Fechar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
